/**
 * UploadPoModal — Three-step PO upload workflow.
 *
 * Step 1: UPLOAD — Drop PDF → AI extracts & matches (READ-ONLY)
 * Step 2: REVIEW — Matches, vendor, email options, "Add to BOM" for unmatched. Cancel = no side effects.
 * Step 3: DONE — Confirmation, set delivery dates, final actions.
 */

import { useState, useRef } from 'react';
import {
  previewPo, confirmPo, updatePoLine, sendPo,
  updatePurchaseOrder,
} from '../../../api/purchasing';
import { Button, Modal } from '../../common';

const STATUS_ICONS = { matched: '✅', unmatched: '⚠️' };

export default function UploadPoModal({ open, onClose, job, onUploaded }) {
  const [step, setStep] = useState('upload');

  // Step 1
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [aiInstructions, setAiInstructions] = useState('');  // Moved to Step 1!

  // Step 2: Preview
  const [preview, setPreview] = useState(null);
  const [emailToVendor, setEmailToVendor] = useState(true);
  const [upsRed, setUpsRed] = useState(false);
  const [doNotShipEnabled, setDoNotShipEnabled] = useState(false);
  const [doNotShipDate, setDoNotShipDate] = useState('');
  const [vendorInstructions, setVendorInstructions] = useState('');
  // Follow-up is automatic — no toggle needed
  const [allDate, setAllDate] = useState('');
  const [perLineDates, setPerLineDates] = useState({});
  const [addToBom, setAddToBom] = useState(false);
  const [oneOffByIndex, setOneOffByIndex] = useState(() => new Set());  // indices into match_results that are one-off
  const [newVendorEmail, setNewVendorEmail] = useState('');
  const [confirming, setConfirming] = useState(false);

  // Step 3
  const [result, setResult] = useState(null);
  const [savingFinal, setSavingFinal] = useState(false);
  // V2-b R4 (amended) — CC actual-supplier selector. Populated from
  // the confirm response (`actual_supplier_candidates`), edited here,
  // persisted via PATCH on Save.
  const [actualSupplier, setActualSupplier] = useState('');
  const [actualSupplierSaving, setActualSupplierSaving] = useState(false);

  const fileInputRef = useRef(null);
  const isCreditCard = preview?.is_credit_card || false;
  // Show email input when vendor is new OR when existing vendor has no email on file
  const needsEmailPrompt = !!preview && !isCreditCard && (!preview.vendor_found || preview.vendor_email_missing);
  const vendorEmail = preview?.vendor_email || newVendorEmail || '';
  const hasVendorEmail = !!vendorEmail;

  // ── Step 1 ──
  function handleDrop(e) {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    const dropped = e.dataTransfer?.files?.[0];
    if (dropped?.type === 'application/pdf') { setFile(dropped); setError(''); }
    else setError('Please drop a PDF file.');
  }

  async function handlePreview() {
    if (!file) return;
    setUploading(true); setError('');
    try {
      const data = await previewPo(job.id, file, aiInstructions.trim());  // Pass AI instructions
      setPreview(data);
      const dates = {};
      (data.match_results || []).forEach((_, i) => { dates[i] = ''; });
      setPerLineDates(dates);
      if (data.is_credit_card) { setEmailToVendor(false); }
      // Default: add unmatched to BOM if there are unmatched lines
      if (data.unmatched_lines > 0) setAddToBom(true);
      setStep('review');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to extract PO. Check the PDF and try again.');
    } finally {
      setUploading(false);
    }
  }

  // ── Step 2 ──
  function handleApplyAllDate(v) {
    setAllDate(v);
    if (v) { const u = {}; Object.keys(perLineDates).forEach(k => { u[k] = v; }); setPerLineDates(u); }
  }

  async function handleConfirm() {
    if (preview?.duplicate_warning) {
      if (!confirm(`Warning: ${preview.duplicate_warning}\n\nProceed anyway?`)) return;
    }
    setConfirming(true); setError('');
    try {
      // Step 1: Create PO in database
      const data = await confirmPo(job.id, file, preview.ai_data, {
        addUnmatchedToBom: addToBom,
        newVendorEmail: needsEmailPrompt ? newVendorEmail : '',
        oneOffPoIndices: addToBom ? [...oneOffByIndex] : [],
      });
      setResult(data);
      // V2-b R4 (amended) — seed the CC actual-supplier selector.
      // The confirm response returns actual_supplier_name (resolver
      // pick, may be blank) and actual_supplier_candidates.
      setActualSupplier(data.actual_supplier_name || '');

      // Step 2: Send PO email to vendor immediately (if enabled)
      if (emailToVendor && !isCreditCard && hasVendorEmail && data?.po_id) {
        try {
          await sendPo(data.po_id, {
            ups_red: upsRed,
            do_not_ship_until: doNotShipEnabled ? doNotShipDate : '',
            vendor_instructions: vendorInstructions.trim(),
          });
        } catch (err) {
          setError(`PO created but email failed: ${err.response?.data?.error || 'Unknown'}. Send manually from PO card.`);
        }
      }

      // Step 3: Save AI instructions
      if (aiInstructions.trim() && data?.po_id) {
        try { await updatePurchaseOrder(data.po_id, { notes: `AI Instructions: ${aiInstructions.trim()}` }); } catch { /* ok */ }
      }

      // Map per-line dates to po_line_ids
      const newDates = {};
      (data.match_results || []).forEach(mr => {
        if (mr.po_line_id) newDates[mr.po_line_id] = allDate || '';
      });
      setPerLineDates(newDates);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process PO.');
    } finally {
      setConfirming(false);
    }
  }

  // ── Step 3 ──
  async function handleSaveFinal() {
    setSavingFinal(true); setError('');
    try {
      // Only save delivery dates — email was already sent in handleConfirm
      const dateUpdates = Object.entries(perLineDates)
        .filter(([, d]) => d)
        .map(([lineId, date]) => updatePoLine(lineId, { expected_delivery_date: date }));
      await Promise.all(dateUpdates);

      // V2-b R4 (amended) — persist the CC actual-supplier pick.
      // Only fires when the operator entered/selected something on
      // a CC PO that resolved to a blank; a blank submission is
      // "skip and let Kevin set it later on the PO detail page".
      if (
        result?.is_credit_card
        && !result?.actual_supplier_name
        && actualSupplier
        && actualSupplier !== (result.actual_supplier_name || '')
      ) {
        setActualSupplierSaving(true);
        try {
          await updatePurchaseOrder(result.po_id, {
            actual_supplier_name: actualSupplier,
          });
        } catch {
          // Non-blocking — the dates + PO exist; supplier can be
          // set from the PO detail page.
        } finally {
          setActualSupplierSaving(false);
        }
      }

      onUploaded?.(); handleReset(); onClose?.();
    } catch {
      setError('Failed to save delivery dates.');
    } finally {
      setSavingFinal(false);
    }
  }

  function handleCancel() { handleReset(); onClose?.(); }

  function handleReset() {
    setStep('upload'); setFile(null); setPreview(null); setResult(null);
    setError(''); setAllDate(''); setPerLineDates({});
    setEmailToVendor(true); setUpsRed(false);
    setDoNotShipEnabled(false); setDoNotShipDate('');
    setVendorInstructions(''); setAiInstructions('');
    setConfirming(false); setSavingFinal(false);
    setAddToBom(false); setOneOffByIndex(new Set()); setNewVendorEmail('');
  }

  if (!open) return null;
  const data = preview || result;
  const matchResults = data?.match_results || [];

  return (
    <Modal open={open} onClose={handleCancel} title="Upload Purchase Order" maxWidth="max-w-2xl">
      <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-1">

        {/* ═══ STEP 1: UPLOAD ═══ */}
        {step === 'upload' && (
          <>
            <p className="text-sm text-gray-600">
              Upload a TrulinX PO PDF. The AI will extract and preview the data before any changes are made.
            </p>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                ${dragOver ? 'border-blue-400 bg-blue-50' : file ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
            >
              <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" onChange={e => { if (e.target.files?.[0]) { setFile(e.target.files[0]); setError(''); } e.target.value = ''; }} className="hidden" />
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <span className="text-green-600 text-lg">📄</span>
                  <div>
                    <p className="text-sm font-medium text-green-800">{file.name}</p>
                    <p className="text-xs text-green-600">{(file.size / 1024).toFixed(1)} KB — Click to change</p>
                  </div>
                </div>
              ) : (
                <div>
                  <svg className="w-10 h-10 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-gray-600 font-medium">Drop a PO PDF here or click to browse</p>
                  <p className="text-xs text-gray-400 mt-1">Drag from email attachments or file explorer</p>
                </div>
              )}
            </div>
            {error && <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>}
            
            {/* AI Instructions - shown BEFORE preview so it affects extraction */}
            {file && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">Special instructions for AI (optional)</label>
                <textarea 
                  value={aiInstructions} 
                  onChange={e => setAiInstructions(e.target.value)} 
                  placeholder="e.g. Only 1000 of 2000 is for this job, the rest is for job 2045…" 
                  rows={2} 
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" 
                />
                <p className="text-xs text-gray-400 mt-1">Tell the AI how to handle qty splits, partial orders, or special matching rules.</p>
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={handleCancel}>Cancel</Button>
              <Button onClick={handlePreview} disabled={!file || uploading} loading={uploading}>
                {uploading ? 'Extracting…' : 'Preview'}
              </Button>
            </div>
            {uploading && <div className="text-center text-xs text-gray-400">AI is extracting PO data — nothing is saved yet. 10-20 seconds.</div>}
          </>
        )}


        {/* ═══ STEP 2: REVIEW ═══ */}
        {step === 'review' && preview && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-2.5 text-center">
              <p className="text-xs font-medium text-blue-700">
                👁 PREVIEW ONLY — nothing has been saved. Review and click "Process PO" to confirm, or "Cancel" to discard.
              </p>
            </div>

            {/* ─── PROMINENT VENDOR BOX ─── */}
            {!isCreditCard && (
              <div className={`rounded-md border-2 p-3 ${
                preview.vendor_found
                  ? 'bg-emerald-50 border-emerald-300'
                  : 'bg-amber-50 border-amber-400'
              }`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {preview.vendor_found ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-emerald-600 text-white tracking-wide">
                          ✓ EXISTING VENDOR
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-amber-600 text-white tracking-wide">
                          ⚠ NEW VENDOR — WILL BE CREATED
                        </span>
                      )}
                      {preview.vendor_match_confidence && (
                        <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${
                          preview.vendor_match_confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
                          preview.vendor_match_confidence === 'medium' ? 'bg-blue-100 text-blue-700' :
                          preview.vendor_match_confidence === 'low' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          AI confidence: {preview.vendor_match_confidence}
                        </span>
                      )}
                    </div>
                    <p className="text-lg font-bold text-gray-900 leading-tight break-words">
                      {preview.vendor_name || '(unknown vendor)'}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Vendor code: <span className="font-mono font-semibold">{preview.vendor_code || '(none)'}</span>
                    </p>
                    {preview.vendor_match_reasoning && (
                      <p className="text-xs text-gray-500 italic mt-1">
                        AI note: {preview.vendor_match_reasoning}
                      </p>
                    )}
                  </div>
                </div>

                {/* Vendor code mismatch warning — AI matched but codes differ */}
                {preview.vendor_code_mismatch && preview.vendor_code_mismatch_info && (
                  <div className="mt-2 p-2 bg-amber-100 border border-amber-400 rounded text-xs">
                    <p className="font-semibold text-amber-900">⚠ Vendor code mismatch — verify before continuing</p>
                    <p className="text-amber-800 mt-0.5">
                      PO shows vendor code <span className="font-mono font-bold">{preview.vendor_code_mismatch_info.extracted_code}</span>,
                      but matched to <span className="font-semibold">{preview.vendor_code_mismatch_info.existing_name}</span> which has code <span className="font-mono font-bold">{preview.vendor_code_mismatch_info.existing_code}</span>.
                      Cancel and fix the vendor record if this is wrong.
                    </p>
                  </div>
                )}

                {/* Email row */}
                <div className="mt-2 pt-2 border-t border-emerald-200">
                  {preview.vendor_found && !preview.vendor_email_missing ? (
                    <p className="text-xs text-gray-700">
                      <span className="text-gray-500">Email on file:</span>{' '}
                      <span className="font-medium">{preview.vendor_email}</span>
                    </p>
                  ) : (
                    <div>
                      <label className="block text-xs font-medium text-amber-800 mb-1">
                        {preview.vendor_found
                          ? `⚠ No email on file for ${preview.vendor_name} — add one to save to vendor record:`
                          : 'Vendor email (will be saved on the new vendor record):'}
                      </label>
                      <input
                        type="email"
                        value={newVendorEmail}
                        onChange={e => setNewVendorEmail(e.target.value)}
                        placeholder="vendor@example.com"
                        className="w-full border border-amber-400 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-amber-500 bg-white"
                      />
                      <p className="text-[10px] text-gray-500 mt-0.5">Leave blank to skip emailing the PO now. You can add it later from the Vendor record.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {preview.duplicate_warning && (
              <div className="bg-red-50 border border-red-300 rounded-md p-3 text-sm text-red-800 font-medium">
                ⚠️ {preview.duplicate_warning}
              </div>
            )}

            {isCreditCard && (
              <div className="bg-purple-50 border border-purple-200 rounded-md p-3 flex items-start gap-2">
                <span className="text-lg">💳</span>
                <div>
                  <p className="text-sm font-semibold text-purple-800">Credit Card Purchase (Whitney Bank Chad)</p>
                  <p className="text-xs text-purple-600">Internal TrulinX purchase — vendor prints as RUELCO. No email will be sent.</p>
                </div>
              </div>
            )}

            {/* Line items */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                    <th className="pb-2 pr-2 w-8"></th>
                    <th className="pb-2 pr-2">Part #</th>
                    <th className="pb-2 pr-2">Description</th>
                    <th className="pb-2 pr-2 text-right">Qty</th>
                    <th className="pb-2 text-right">Price</th>
                    {addToBom && (
                      <th className="pb-2 pl-2 text-center text-violet-700" title="Mark as one-off so this line is excluded from Save as Version">
                        One-off?
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {matchResults.map((line, i) => (
                    <tr key={i} className={`border-b border-gray-100 ${!line.matched ? 'bg-amber-50/50' : ''}`}>
                      <td className="py-2 pr-2">{STATUS_ICONS[line.matched ? 'matched' : 'unmatched']}</td>
                      <td className="py-2 pr-2 font-mono text-xs">
                        {line.item_number || line.vendor_part_number || '—'}
                        {line.matched && line.bom_line_part && (
                          <span className="text-gray-400 ml-1">→ {line.bom_line_part}</span>
                        )}
                      </td>
                      <td className="py-2 pr-2 text-gray-600 text-xs max-w-[180px] truncate">{line.description || '—'}</td>
                      <td className="py-2 pr-2 text-right">{line.qty}</td>
                      <td className="py-2 text-right">${Number(line.price).toFixed(2)}</td>
                      {addToBom && (
                        <td className="py-2 pl-2 text-center">
                          {line.matched ? (
                            <span className="text-gray-300">—</span>
                          ) : (
                            <input
                              type="checkbox"
                              checked={oneOffByIndex.has(i)}
                              onChange={e => {
                                setOneOffByIndex(prev => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(i); else next.delete(i);
                                  return next;
                                });
                              }}
                              className="w-4 h-4 text-pink-600 rounded border-gray-300 focus:ring-pink-500"
                              title="Check to mark this new BOM line as one-off (excluded from Save as Version)"
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ─── ADD UNMATCHED TO BOM (in review step!) ─── */}
            {preview.unmatched_lines > 0 && (
              <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                addToBom ? 'bg-violet-50 border-violet-300' : 'bg-gray-50 border-gray-200'
              }`}>
                <input
                  type="checkbox"
                  checked={addToBom}
                  onChange={e => setAddToBom(e.target.checked)}
                  className="w-4 h-4 text-violet-600 rounded mt-0.5"
                />
                <div>
                  <p className={`text-sm font-medium ${addToBom ? 'text-violet-800' : 'text-gray-700'}`}>
                    Add {preview.unmatched_lines} unmatched item{preview.unmatched_lines > 1 ? 's' : ''} to BOM
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {addToBom
                      ? 'New part numbers and vendor links will be created automatically. Parts flagged for review.'
                      : 'Unmatched items will be on the PO but NOT added to the BOM. Use this if items are for stock or another job.'}
                  </p>
                </div>
              </label>
            )}

            {/* ─── VENDOR COMMUNICATION (hidden for credit card) ─── */}
            {!isCreditCard && (
              <div className="space-y-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Vendor Communication</p>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={emailToVendor} onChange={e => setEmailToVendor(e.target.checked)} className="w-4 h-4 text-blue-600 rounded mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">Email PO to vendor</p>
                    {hasVendorEmail
                      ? <p className="text-xs text-gray-500">Will send to: <span className="font-medium text-gray-700">{vendorEmail}</span></p>
                      : <p className="text-xs text-red-500 font-medium">⚠ No email on file for {preview.vendor_name}</p>}
                  </div>
                </label>

                {emailToVendor && hasVendorEmail && (
                  <div className="ml-7 space-y-2">
                    <label className={`flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition-colors ${upsRed ? 'bg-red-50 border-red-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                      <input type="checkbox" checked={upsRed} onChange={e => setUpsRed(e.target.checked)} className="w-4 h-4 text-red-600 rounded" />
                      <span className={`text-sm font-semibold ${upsRed ? 'text-red-700' : 'text-gray-600'}`}>
                        {upsRed ? '🔴 UPS RED — NEXT DAY AIR' : 'UPS RED — Next Day Air'}
                      </span>
                    </label>
                    {!upsRed && <p className="text-xs text-gray-400 ml-1">Default: Ship Collect — UPS Acct# Y40195 / Motor Freight: SAIA Collect Acct# 1005512</p>}

                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={doNotShipEnabled} onChange={e => { setDoNotShipEnabled(e.target.checked); if (!e.target.checked) setDoNotShipDate(''); }} className="w-4 h-4 text-blue-600 rounded" />
                        <span className="text-sm text-gray-700">Do not ship until</span>
                      </label>
                      {doNotShipEnabled && <input type="date" value={doNotShipDate} onChange={e => setDoNotShipDate(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm" />}
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Special instructions to vendor</label>
                      <textarea value={vendorInstructions} onChange={e => setVendorInstructions(e.target.value)} placeholder="e.g. Ship to job site, include packing list…" rows={2} className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3 px-1 py-1">
                  <span className="text-blue-500 text-sm">ℹ</span>
                  <p className="text-xs text-gray-500">
                    Auto follow-up is enabled for all POs with delivery dates. The system will request status updates from the vendor a few days before the expected delivery date.
                  </p>
                </div>
              </div>
            )}

            {/* ─── VENDOR COMMUNICATION (hidden for credit card) ─── */}
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Expected delivery (all items):</label>
                <input type="date" value={allDate} onChange={e => handleApplyAllDate(e.target.value)} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
                {allDate && <span className="text-xs text-green-600 font-medium">Applied to all</span>}
              </div>
              <p className="text-xs text-gray-400 mt-1">Delivery dates will be saved after processing.</p>
            </div>

            {error && <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>}

            {/* Actions — bottom bar */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-200 sticky bottom-0 bg-white pb-1">
              <div className="text-xs text-gray-400">
                {emailToVendor && hasVendorEmail && !isCreditCard
                  ? `Will email to ${vendorEmail}${upsRed ? ' (UPS RED)' : ''}`
                  : isCreditCard ? 'Credit card — no email' : 'PO will not be emailed'}
                {addToBom && preview.unmatched_lines > 0 ? ` • ${preview.unmatched_lines} added to BOM` : ''}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={handleCancel}>Cancel</Button>
                <Button onClick={handleConfirm} loading={confirming}>
                  {confirming ? 'Processing…' : emailToVendor && hasVendorEmail && !isCreditCard ? 'Process & Send to Vendor' : 'Process PO'}
                </Button>
              </div>
            </div>
          </>
        )}


        {/* ═══ STEP 3: DONE ═══ */}
        {step === 'done' && result && (
          <>
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <p className="text-sm font-semibold text-green-800">✅ PO {result.po_number} processed successfully</p>
              <p className="text-xs text-green-700">
                {result.matched_lines} of {result.total_lines} matched
                {result.rfqs_closed > 0 && ` • ${result.rfqs_closed} RFQ${result.rfqs_closed > 1 ? 's' : ''} closed`}
                {result.bom_add_result?.added > 0 && ` • ${result.bom_add_result.added} item${result.bom_add_result.added > 1 ? 's' : ''} added to BOM`}
              </p>
            </div>

            {/* V2-b R4 (amended) — CC actual-supplier selector.
                Shows only when the PO is credit-card AND the resolver
                did NOT pick a single supplier automatically (either
                zero preferred VendorParts, or preferences disagreed).
                Optional — Kevin can skip and set it later from the
                PO detail page. Candidates come pre-populated from the
                resolver; if empty, he still gets a text input so the
                edit remains possible. */}
            {result.is_credit_card && !result.actual_supplier_name && (
              <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
                <label className="block text-sm font-semibold text-purple-800 mb-1">
                  Actual supplier (optional — credit-card PO)
                </label>
                <p className="text-xs text-purple-700 mb-2">
                  {(result.actual_supplier_candidates || []).length > 0
                    ? "The parts on this PO don't all prefer the same vendor. Pick who actually shipped, or skip and set it later on the PO detail page."
                    : "No preferred vendor is on file for the parts on this PO. Type the supplier name, or skip and set it later."}
                </p>
                {(result.actual_supplier_candidates || []).length > 0 ? (
                  <select
                    value={actualSupplier}
                    onChange={e => setActualSupplier(e.target.value)}
                    className="w-full border border-purple-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="">— skip —</option>
                    {(result.actual_supplier_candidates || []).map(c => (
                      <option key={c.id} value={c.name}>
                        {c.name}{c.code ? ` (${c.code})` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={actualSupplier}
                    onChange={e => setActualSupplier(e.target.value)}
                    placeholder="Supplier name (optional)"
                    className="w-full border border-purple-300 rounded px-2 py-1 text-sm"
                  />
                )}
              </div>
            )}

            {/* BOM additions detail */}
            {result.bom_add_result?.items?.length > 0 && (
              <div className="bg-violet-50 border border-violet-200 rounded-md p-3">
                <p className="text-sm font-medium text-violet-800">📋 Parts added to BOM:</p>
                {result.bom_add_result.items.map((item, i) => (
                  <p key={i} className="text-xs text-violet-700 mt-0.5">
                    {item.internal_pn}{item.created_part ? ' (new part)' : ''} — qty {item.qty}
                  </p>
                ))}
              </div>
            )}

            {/* Per-line delivery dates */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                    <th className="pb-2 pr-2 w-8"></th>
                    <th className="pb-2 pr-2">Part #</th>
                    <th className="pb-2 pr-2 text-right">Qty</th>
                    <th className="pb-2 pr-2 text-right">Price</th>
                    <th className="pb-2">Delivery Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.match_results || []).map((line, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-2 pr-2">{STATUS_ICONS[line.matched ? 'matched' : 'unmatched']}</td>
                      <td className="py-2 pr-2 font-mono text-xs">
                        {line.item_number || line.vendor_part_number || '—'}
                        {/* Phase E §10.2 — small badge on paint-service
                            lines that auto-received their cost. Visual
                            cue so the operator sees the special path
                            fired (and that no delivery-date prompt is
                            needed for this line). */}
                        {line.paint_service_auto_received && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200"
                            title="Paint cost applied to BOM line & auto-received."
                          >
                            🎨 auto-received
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-2 text-right">{line.qty}</td>
                      <td className="py-2 pr-2 text-right">${Number(line.price).toFixed(2)}</td>
                      <td className="py-2">
                        {line.paint_service_auto_received ? (
                          <span className="text-xs text-purple-600">received on PO confirm</span>
                        ) : line.po_line_id ? (
                          <input type="date"
                            value={perLineDates[line.po_line_id] || allDate || ''}
                            onChange={e => setPerLineDates(d => ({ ...d, [line.po_line_id]: e.target.value }))}
                            className="w-36 border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {error && <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>}

            <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 sticky bottom-0 bg-white pb-1">
              <Button variant="ghost" onClick={() => { onUploaded?.(); handleReset(); onClose?.(); }}>Skip Dates</Button>
              <Button onClick={handleSaveFinal} loading={savingFinal}>
                {savingFinal ? 'Saving…' : 'Save & Done'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
